import {useState} from 'react';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {Box, Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {translations} from '../../../../../../i18n/translations';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {GdprErasureRequestsRepository} from '../../../../../../repositories/gdpr-erasure-requests';
import {RightToErasureRequest} from '../../../../../../backend/interfaces/right-to-erasure-request';
import {RightToErasureRequestStatus} from '../../../../../../backend/enums/right-to-erasure-request-status';
import {CreateErasureRequestModal} from './create-erasure-request-modal';

// Columns IDs
enum ColumnIdentifier {
  Status = 'status',
  RequestedAt = 'requestedAt',
  CompletedAt = 'completedAt',
  ErrorMessage = 'errorMessage',
  UserIdHash = 'userIdHash',
  UserEmailAddressHash = 'userEmailAddressHash',
}

// Truncate a hash so it can only be used as a technical reference, never reversed into personal data
const truncateHash = (hash: string): string => (hash.length > 14 ? `${hash.slice(0, 12)}…` : hash);

export default function GdprErasureRequestsSystemAdministratorOfficeScreen() {
  const {t} = useTranslation();
  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);

  const statusLabel = (status: RightToErasureRequestStatus): string => {
    switch (status) {
      case RightToErasureRequestStatus.Completed:
        return t(translations.gdpr_erasure_requests.root.status_completed);
      case RightToErasureRequestStatus.Failed:
        return t(translations.gdpr_erasure_requests.root.status_failed);
      case RightToErasureRequestStatus.Processing:
      default:
        return t(translations.gdpr_erasure_requests.root.status_processing);
    }
  };

  // Fetch erasure requests
  const handleGetErasureRequests = async (limit: number, skip: number) => {
    const result: {totalCount: number; data: Array<RightToErasureRequest>} =
      await GdprErasureRequestsRepository.instance.listErasureRequests(limit, skip);

    // Process data
    const processedData: PaginatedTableData<RightToErasureRequest> = result.data.map((obj) => {
      return {
        id: obj.id || '',
        item: obj,
        columns: [
          {
            columnId: ColumnIdentifier.Status,
            value: statusLabel(obj.status),
          },
          {
            columnId: ColumnIdentifier.RequestedAt,
            value: obj.requestedAt ? moment(obj.requestedAt).format('DD/MM/YYYY HH:mm') : '',
          },
          {
            columnId: ColumnIdentifier.CompletedAt,
            value: obj.completedAt ? moment(obj.completedAt).format('DD/MM/YYYY HH:mm') : '',
          },
          {
            columnId: ColumnIdentifier.ErrorMessage,
            value: obj.status === RightToErasureRequestStatus.Failed ? obj.errorMessage || '' : '',
          },
          {
            columnId: ColumnIdentifier.UserIdHash,
            value: obj.userIdHash ? truncateHash(obj.userIdHash) : '',
          },
          {
            columnId: ColumnIdentifier.UserEmailAddressHash,
            value: obj.userEmailAddressHash ? truncateHash(obj.userEmailAddressHash) : '',
          },
        ],
      };
    });

    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  const {data, isLoading, error, pagesCount, currentPage, handlePageChange, handleApplyFilters} =
    usePaginatedTable<RightToErasureRequest>({
      fetchData: handleGetErasureRequests,
    });

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        isLoading={false}
        pageTitle={t(translations.gdpr_erasure_requests.root.title)}
        leftColumn={<Stack sx={{width: '100%', paddingTop: '10px'}} />}
        rightColumn={
          <Button
            endIcon={<AddIcon />}
            onClick={() => setCreateModalOpened(true)}
            variant="inverted"
          >
            {t(translations.gdpr_erasure_requests.root.add_button)}
          </Button>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.gdpr_erasure_requests.root.no_requests)}
            columns={[
              {
                columnId: ColumnIdentifier.Status,
                title: t(translations.gdpr_erasure_requests.root.status_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.RequestedAt,
                title: t(translations.gdpr_erasure_requests.root.requested_at_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.CompletedAt,
                title: t(translations.gdpr_erasure_requests.root.completed_at_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.ErrorMessage,
                title: t(translations.gdpr_erasure_requests.root.error_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.UserIdHash,
                title: t(translations.gdpr_erasure_requests.root.user_id_hash_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.UserEmailAddressHash,
                title: t(translations.gdpr_erasure_requests.root.user_email_hash_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={false}
          />
        </Box>
      </Box>
      <CreateErasureRequestModal
        open={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        onCreated={() => handleApplyFilters(1)}
      />
    </Box>
  );
}
